import { FirebaseErrorCodes } from "@elit-hair/elitklinik-shared";
import { FirebaseManagerError, firebaseManager } from "@elit-hair/elitklinik-shared/notification";
import styled from "@emotion/styled";
import { MaterialDesignContent, SnackbarProvider } from "notistack";
import { enqueueSnackbar } from "notistack";
import { ReactNode, useEffect } from "react";

import { QUERY_KEYS } from "@/constants";
import { firebaseConfig, firebaseVapidKey } from "@/constants/firebase";
import { useCapacitor } from "@/hooks/useCapacitor";
import { queryClient } from "@/main";
import { useNotificationSubscribeMutation } from "@/requests/notification.queries";

import useAuth from "../hooks/useAuth";

const alertAudio = new Audio("/sounds/alert.mp3");

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
    const { auth } = useAuth();

    const { isCordova } = useCapacitor();
    const { mutateAsync: subscribe } = useNotificationSubscribeMutation();

    useEffect(() => {
        if (!auth) return;

        try {
            const { accessToken } = auth;
            if (!accessToken) return;
            firebaseManager.onError = (err) => {
                console.error(err, "#onError");
                if (
                    err.code === FirebaseErrorCodes.MESSAGING_PERMISSION_BLOCKED ||
                    err.code === FirebaseErrorCodes.MESSAGING_NOT_SUPPORTED
                )
                    return;
                enqueueSnackbar(err.message, { variant: "error" });
            };

            firebaseManager.onMessage = (payload) => {
                console.info(payload, "#onMessage");
                alertAudio.play();
                queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_UNREAD_COUNT] });
                queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_NOTIFICATIONS] });
            };

            firebaseManager.onTokenReceived = (token) => {
                subscribe(token);
            };
            firebaseManager.initMessaging(firebaseVapidKey, firebaseConfig);
        } catch (err) {
            if (err instanceof FirebaseManagerError) {
                enqueueSnackbar(err.message, { variant: "error" });
            }
            console.error(err, "#err");
        }

        return () => {
            firebaseManager.cleanup();
        };
    }, [auth, subscribe]);

    const StyledMaterialDesignContent = styled(MaterialDesignContent)(() => ({
        "&.notistack-MuiContent-default": {
            backgroundColor: "#054680",
            borderRadius: 16,
            boxShadow: "0px 0px 10px 0px rgba(0,0,0,0.75)",
        },
        "&.notistack-MuiContent-success": {
            borderRadius: 16,
            boxShadow: "0px 0px 10px 0px rgba(0,0,0,0.75)",
        },
        "&.notistack-MuiContent-error": {
            borderRadius: 16,
            boxShadow: "0px 0px 10px 0px rgba(0,0,0,0.75)",
        },
    }));

    return (
        <SnackbarProvider
            maxSnack={50}
            autoHideDuration={3000}
            anchorOrigin={{ vertical: isCordova ? "top" : "top", horizontal: isCordova ? "right" : "right" }}
            dense
            SnackbarProps={{
                style: { marginTop: isCordova ? 30 : 0 },
            }}
            Components={{
                default: StyledMaterialDesignContent,
                success: StyledMaterialDesignContent,
                error: StyledMaterialDesignContent,
            }}
        >
            {children}
        </SnackbarProvider>
    );
};
